import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Users, Plus, UserPlus } from 'lucide-react';

const UserManagementPage = () => {
  const { API, user: currentUser } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    vorname: '',
    role: 'helper',
    temp_password: '',
  });
  const [editFormData, setEditFormData] = useState({
    email: '',
    name: '',
    vorname: '',
    role: 'helper',
    new_password: '',
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Nutzer');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    // Validierung
    if (!formData.email || !formData.name || !formData.temp_password) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }
    
    if (formData.temp_password.length < 6) {
      toast.error('Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }
    
    try {
      // Backend erwartet: email, name, role, password
      const payload = {
        email: formData.email.trim().toLowerCase(),
        name: formData.vorname ? `${formData.vorname} ${formData.name}`.trim() : formData.name.trim(),
        role: formData.role,
        password: formData.temp_password,  // Backend erwartet "password", nicht "temp_password"
      };
      
      await axios.post(`${API}/users`, payload);
      toast.success('Nutzer erfolgreich erstellt!');
      setShowCreateDialog(false);
      fetchUsers();
      setFormData({
        email: '',
        name: '',
        vorname: '',
        role: 'helper',
        temp_password: '',
      });
    } catch (error) {
      // Detaillierte Fehlerbehandlung
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      
      if (status === 400 && detail?.includes('Email already')) {
        toast.error('Diese E-Mail-Adresse ist bereits registriert');
      } else if (status === 409) {
        toast.error('Benutzer existiert bereits');
      } else if (status === 422) {
        toast.error('Ungültige Eingabedaten. Bitte prüfen Sie alle Felder.');
      } else if (detail) {
        toast.error(detail);
      } else {
        toast.error('Fehler beim Erstellen des Nutzers');
      }
      console.error('[UserManagement] Create user error:', { status, detail, error });
    }
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let password = '';
    for (let i = 0; i < 10; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({ ...formData, temp_password: password });
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setEditFormData({
      email: user.email,
      name: user.name,
      vorname: user.vorname || user.name,
      role: user.role,
      new_password: '',
    });
    setShowEditDialog(true);
  };

  const generateEditPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let password = '';
    for (let i = 0; i < 10; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setEditFormData({ ...editFormData, new_password: password });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/users/${selectedUser.id}`, editFormData);
      toast.success('Nutzer aktualisiert!');
      setShowEditDialog(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren');
    }
  };

  const handleDeleteClick = (user) => {
    if (user.id === currentUser.id) {
      toast.error('Sie können sich nicht selbst löschen');
      return;
    }
    setUserToDelete(user);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    try {
      await axios.delete(`${API}/users/${userToDelete.id}`);
      toast.success('Nutzer gelöscht!');
      setShowDeleteDialog(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Laden...</div>;
  }

  return (
    <div className="space-y-6 animate-slide-in" data-testid="user-management-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk' }}>
            Nutzerverwaltung
          </h1>
          <p className="text-slate-600">Verwalte Benutzer und deren Zugriffsrechte</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="flex items-center space-x-2 bg-gradient-to-r from-blue-500 to-indigo-600" data-testid="create-user-button">
              <Plus size={20} />
              <span>Neuer Nutzer</span>
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Neuen Nutzer anlegen</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Vorname</Label>
                  <Input
                    value={formData.vorname}
                    onChange={(e) => setFormData({ ...formData, vorname: e.target.value })}
                    required
                    placeholder="Max"
                  />
                  <p className="text-xs text-slate-500 mt-1">Wird als Benutzername verwendet</p>
                </div>
                <div>
                  <Label>Nachname</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="Mustermann"
                  />
                </div>
              </div>
              <div>
                <Label>E-Mail</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  placeholder="max@beispiel.de"
                />
              </div>
              <div>
                <Label>Rolle</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="helper">Helfer</SelectItem>
                    <SelectItem value="admin">Administrator</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Passwort</Label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={formData.temp_password}
                    onChange={(e) => setFormData({ ...formData, temp_password: e.target.value })}
                    required
                    placeholder="Passwort"
                  />
                  <Button type="button" variant="outline" onClick={generatePassword}>
                    Generieren
                  </Button>
                </div>
              </div>
              <Button type="submit" className="w-full" data-testid="submit-create-user">
                Nutzer anlegen
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nutzer bearbeiten</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Vorname</Label>
                  <Input
                    value={editFormData.vorname}
                    onChange={(e) => setEditFormData({ ...editFormData, vorname: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Nachname</Label>
                  <Input
                    value={editFormData.name}
                    onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div>
                <Label>E-Mail</Label>
                <Input
                  type="email"
                  value={editFormData.email}
                  onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Rolle</Label>
                <Select value={editFormData.role} onValueChange={(value) => setEditFormData({ ...editFormData, role: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="helper">Helfer</SelectItem>
                    <SelectItem value="admin">Administrator</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                <Label className="text-slate-700 font-semibold mb-2">Neues Passwort vergeben (optional)</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    type="text"
                    value={editFormData.new_password}
                    onChange={(e) => setEditFormData({ ...editFormData, new_password: e.target.value })}
                    placeholder="Leer lassen für keine Änderung"
                  />
                  <Button type="button" onClick={generateEditPassword} variant="outline" size="sm">
                    Generieren
                  </Button>
                </div>
              </div>
              
              <Button type="submit" className="w-full" data-testid="submit-update-user">
                Änderungen speichern
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nutzer löschen</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 font-medium mb-2">⚠️ Achtung!</p>
                <p className="text-red-700 text-sm">
                  Diese Aktion kann nicht rückgängig gemacht werden.
                </p>
              </div>
              {userToDelete && (
                <p className="text-slate-600">
                  Möchten Sie den Benutzer <strong>{userToDelete.email}</strong> wirklich löschen?
                </p>
              )}
              <div className="flex gap-2">
                <Button
                  onClick={handleConfirmDelete}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  data-testid="confirm-delete-user"
                >
                  Ja, löschen
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDeleteDialog(false);
                    setUserToDelete(null);
                  }}
                  className="flex-1"
                >
                  Abbrechen
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {users.map((user) => (
          <Card key={user.id} className="p-6" data-testid={`user-card-${user.email}`}>
            <div className="flex items-start space-x-3 mb-4">
              <div className="bg-blue-100 p-3 rounded-xl">
                <UserPlus className="text-blue-600" size={24} />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-lg mb-1">{user.vorname || user.name}</h3>
                <p className="text-sm text-slate-500">{user.email}</p>
                {user.username && (
                  <p className="text-xs text-slate-400 mt-1">@{user.username}</p>
                )}
              </div>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between items-center pt-2 border-t">
                <span className="text-slate-600">Rolle:</span>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    user.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'
                  }`}
                >
                  {user.role === 'admin' ? 'Administrator' : 'Helfer'}
                </span>
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={() => handleEdit(user)}
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  data-testid={`edit-user-${user.email}`}
                >
                  Bearbeiten
                </Button>
                <Button
                  onClick={() => handleDeleteClick(user)}
                  variant="outline"
                  size="sm"
                  className="flex-1 text-red-600 hover:bg-red-50"
                  disabled={user.id === currentUser?.id}
                  data-testid={`delete-user-${user.email}`}
                >
                  Löschen
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default UserManagementPage;
